import { FC, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCrmBoardData } from 'src/hooks/subscription/useCrmBoardData';
import useStoreById from 'src/hooks/swr/useStoreById';

import classes from './CrmBoard.module.scss';

import { CrmBoardProvider, useCrmBoardContext } from './CrmBoardContext';
import CreateFormSection from './components/CreateFormSection/CreateFormSection';
import StoreSection from './components/StoreSection/StoreSection';
import TableSection from './components/TableSection/TableSection';

const CrmBoardContent: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedStore, setSelectedStore } = useCrmBoardContext();
  const initializedRef = useRef(false);

  // URL에서 storeId 읽기
  const urlStoreId = searchParams.get('storeId');
  const { store: urlStore } = useStoreById(urlStoreId ?? undefined);

  const { dateRange, setDateRange, issueStatuses, setIssueStatuses, rowData, isLoading, setStoreId } =
    useCrmBoardData();

  // URL의 storeId로 초기 매장 설정
  useEffect(() => {
    if (!initializedRef.current && urlStoreId && urlStore) {
      setSelectedStore({ id: urlStore._id, name: urlStore.storeNickname });
      initializedRef.current = true;
    }
  }, [urlStoreId, urlStore, setSelectedStore]);

  // selectedStore 변경 시 URL 업데이트
  useEffect(() => {
    if (initializedRef.current || !urlStoreId) {
      const newParams = new URLSearchParams(searchParams);
      if (selectedStore?.id) {
        newParams.set('storeId', selectedStore.id);
      } else {
        newParams.delete('storeId');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [selectedStore?.id, searchParams, setSearchParams, urlStoreId]);

  // Context의 selectedStore가 변경될 때마다 데이터 훅의 storeId 동기화
  useEffect(() => {
    setStoreId(selectedStore?.id ?? null);
  }, [selectedStore?.id, setStoreId]);

  return (
    <div className={classes.container}>
      <div className={classes.leftSection}>
        <StoreSection />
        <CreateFormSection />
      </div>
      <div className={classes.rightSection}>
        <TableSection
          rowData={rowData}
          isLoading={isLoading}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          issueStatuses={issueStatuses}
          onIssueStatusesChange={setIssueStatuses}
        />
      </div>
    </div>
  );
};

const CrmBoard: FC = () => {
  return (
    <CrmBoardProvider>
      <CrmBoardContent />
    </CrmBoardProvider>
  );
};

export default CrmBoard;
